/* @flow */

import React from 'react'
import { connect } from 'react-redux'

import type { CheckedoutPoll, Vector } from '../types'

import { openSnippetCreation } from '../stores/pollBuilderIconPicker'
import { clearCurrentIcon } from '../stores/icons/current'
import { moveCheckedoutExpertOpinionWhereIndex } from '../stores/pollBuilderCheckedoutExpertOpinions'
import {
  setExpertOpinionCreateHelper,
  selectExpertOpinionCreateHelper,
} from '../stores/pollBuilderExpertOpinionCreateHelper'
import { setPositionOfNewExpertOpinion } from '../stores/pollBuilderSelectedExpertOpinion'

import {
  getEventPositionRelativeToComponent,
  mouseDragHelper,
  subtractVectors,
  divideVectorByScalar,
} from '../helpers'
import {
  isPoll1D,
  isPoll2D,
  closestValidExpertOpinionPosition,
} from '../stores/pollsStore'
import { DEFAULT_ICON_SIZE } from '../stores/iconsStore'
import ExpertOpinionSVGImage from './ExpertOpinionSVGImage'
import SVGUpperHalfCircle from './SVGUpperHalfCircle'
import ExpertOpinionCreateHelper from './ExpertOpinionCreateHelper'

const padding = 60
const radius = 170

const width = radius * 2 + padding * 2
const height = radius * 2 + padding * 2

function getCircleCenterToCursor(e: Event, svgComponent: Object): Vector {
  const svgToCursor = getEventPositionRelativeToComponent(e, svgComponent)

  const svgToCircleCenter = {
    x: padding + radius,
    y: padding + radius,
  }

  return subtractVectors(svgToCursor, svgToCircleCenter)
}

type Props = {
  poll: CheckedoutPoll,
  expertOpinionCreateHelper: ?Vector,
  moveExpertOpinionWhereIndex: (index: number, position: Vector) => void,
  setExpertOpinionCreateHelper: (position: ?Vector) => void,
  openSnippetCreation: () => void,
  clearCurrentIcon: () => void,
  setPositionOfNewExpertOpinion: (position: ?Vector) => void,
  onIconClick: (expertOpinion: Object) => void,
}

/*
 * renders a poll for the poll edit page
 */
// we can't use a stateless function here because we need `this`
// in order to `getEventPositionRelativeToComponent`.
// eslint is not (yet) smart enough to figure this out. therefore:
// eslint-disable-next-line react/prefer-stateless-function
export class PollSketch extends React.Component<Props> {
  props: Props

  expertOpinionDragHandler(expertOpinion: Object, index: number) {
    // remember vector from center of dragged icon to cursor position
    let iconCenterToCursor
    let moved = false
    return mouseDragHelper(e => {
      e.preventDefault()
      if (e.type === 'mousedown') {
        // vector from top left corner of dragged icon to cursor position
        const iconTopLeftToCursor = getEventPositionRelativeToComponent(
          e,
          e.target
        )
        const iconRect = e.target.getBoundingClientRect()
        const iconTopLeftToCenter = {
          x: (iconRect.right - iconRect.left) / 2,
          y: (iconRect.bottom - iconRect.top) / 2,
        }
        iconCenterToCursor = subtractVectors(
          iconTopLeftToCursor,
          iconTopLeftToCenter
        )
      }
      if (e.type === 'mousemove') {
        moved = true
        const circleCenterToCursor = getCircleCenterToCursor(e, this)

        const circleCenterToIconCenter = subtractVectors(
          circleCenterToCursor,
          iconCenterToCursor
        )

        const circleCenterToIconCenterNormalized = divideVectorByScalar(
          circleCenterToIconCenter,
          radius
        )

        const position = closestValidExpertOpinionPosition(
          this.props.poll,
          circleCenterToIconCenterNormalized
        )

        this.props.moveExpertOpinionWhereIndex(index, position)
      }

      if (e.type === 'mouseup' && !moved) {
        if (this.props.onIconClick) this.props.onIconClick(expertOpinion)
      }
    })
  }

  render() {
    const {
      poll,
      expertOpinionCreateHelper,
      setPositionOfNewExpertOpinion,

      setExpertOpinionCreateHelper,
      openSnippetCreation,
      clearCurrentIcon,
      // eslint-disable-next-line no-unused-vars
      moveExpertOpinionWhereIndex,
      // eslint-disable-next-line no-unused-vars
      onIconClick,
      ...otherProps
    } = this.props

    const fillColor = '#EEEEEE'

    const translate = `translate(${padding}, ${padding})`

    const getExpertOpinionCreateHelperPosition = (e: Event): Vector => {
      const circleCenterToCursor = getCircleCenterToCursor(e, this)
      const circleCenterToCursorNormalized = divideVectorByScalar(
        circleCenterToCursor,
        radius
      )
      return closestValidExpertOpinionPosition(
        this.props.poll,
        circleCenterToCursorNormalized
      )
    }

    const boundingShapeProps = {
      cx: radius,
      cy: radius,
      r: radius,
      fill: fillColor,
    }

    function unsetExpertOpinionCreateHelper() {
      setExpertOpinionCreateHelper(null)
    }

    function setExpertOpinionEventWrapper(e: Event) {
      const position = getExpertOpinionCreateHelperPosition(e)
      setExpertOpinionCreateHelper(position)
    }

    const svgProps = {
      onMouseEnter: setExpertOpinionEventWrapper,
      onMouseMove(e: Event) {
        if (expertOpinionCreateHelper != null) {
          const position = getExpertOpinionCreateHelperPosition(e)
          setExpertOpinionCreateHelper(position)
        }
      },
      onMouseLeave: unsetExpertOpinionCreateHelper,
    }

    return (
      <svg
        width={width}
        height={isPoll1D(poll) ? height / 2 : height}
        {...svgProps}
        {...otherProps}
      >
        <g transform={translate}>
          {(() => {
            if (isPoll1D(poll)) {
              return (
                <SVGUpperHalfCircle
                  {...boundingShapeProps}
                  stroke={fillColor}
                  strokeWidth="20"
                />
              )
            } else if (isPoll2D(poll)) {
              return (
                <image
                  xlinkHref={require('./PollForm/compass_creation.svg')}
                  {...boundingShapeProps}
                  width={boundingShapeProps.r * 2}
                  height={boundingShapeProps.r * 2}
                />
              )
            }

            return null
          })()}
          {(() => {
            if (expertOpinionCreateHelper == null) return null

            function expertOpinionCreateHelperClick() {
              setPositionOfNewExpertOpinion(expertOpinionCreateHelper)
              unsetExpertOpinionCreateHelper()
              clearCurrentIcon()
              openSnippetCreation()
            }

            return (
              <ExpertOpinionCreateHelper
                position={expertOpinionCreateHelper}
                iconSize={DEFAULT_ICON_SIZE}
                sketchRadius={radius}
                onClick={expertOpinionCreateHelperClick}
              />
            )
          })()}
          {(() => {
            if (poll.expertOpinions == null) {
              throw new Error('missing .expertOpinions on poll')
            }
            return poll.expertOpinions.map((expertOpinion, index) => {
              return (
                <ExpertOpinionSVGImage
                  onMouseDown={this.expertOpinionDragHandler(
                    expertOpinion,
                    index
                  )}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={unsetExpertOpinionCreateHelper}
                  onMouseLeave={setExpertOpinionCreateHelper}
                  expertOpinion={expertOpinion}
                  iconSize={DEFAULT_ICON_SIZE}
                  sketchRadius={radius}
                  key={index}
                />
              )
            })
          })()}
        </g>
        {isPoll2D(poll) ? (
          <svg>
            <text
              x={width / 2}
              y={padding / 2}
              textAnchor="middle"
              alignmentBaseline="hanging"
              fill="gray"
            >
              {poll.labelTop}
            </text>
            <text
              x={width / 2}
              y={height - padding / 2}
              textAnchor="middle"
              alignmentBaseline="hanging"
              fill="gray"
            >
              {poll.labelBottom}
            </text>
            <text
              x={-height / 2}
              y={padding / 2}
              transform="rotate(270)"
              textAnchor="middle"
              alignmentBaseline="central"
              fill="gray"
            >
              {poll.labelLeft}
            </text>
            <text
              x={height / 2}
              y={-(width - padding / 2)}
              transform="rotate(90)"
              textAnchor="middle"
              alignmentBaseline="central"
              fill="gray"
            >
              {poll.labelRight}
            </text>
          </svg>
        ) : null}
      </svg>
    )
  }
}

export default connect(
  state => ({
    expertOpinionCreateHelper: selectExpertOpinionCreateHelper(state),
  }),
  {
    moveExpertOpinionWhereIndex: moveCheckedoutExpertOpinionWhereIndex,
    openSnippetCreation,
    clearCurrentIcon,
    setPositionOfNewExpertOpinion,
    setExpertOpinionCreateHelper,
  }
)(PollSketch)



// WEBPACK FOOTER //
// ./src/components/PollSketch.js